import { BoxV2 as Box, CampaignAttributes, Flex } from 'portal-commons';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { faSquarePollHorizontal } from '@fortawesome/pro-regular-svg-icons';
import { Grid } from '@material-ui/core';

import HeadingBlock from './HeadingBlock';
import EmbeddedLinkPopup from '../EmbeddedLinkPopup';
import MessageFlowAttributePopup from '../MessageFlowAttributePopup';
import { CampaignDetailsFormValues, MnoMetadata } from '../../types';
import { isFieldGreyOut } from '../../utils';
import {
  createMaxLengthValidator,
  createMinLengthValidator,
  createRequiredValidator,
  isOptionRequired,
  isValidInput,
} from '../../validators';
import { DEFAULT_CAMPAIGN_FOR_CREATION_FORM } from '@/applications/CspPortal/Campaigns/constants';

interface CampaignAttributesSectionProps {
  greyOutFields: string[];
  mnos: MnoMetadata[];
  termsAndConditions: string[];
}

enum AdditionalSetupType {
  None,
  SubscriberOptin,
  SubscriberOptout,
  SubscriberHelp,
  EmbeddedLink,
}

const CampaignAttributesSection: FunctionComponent<
  CampaignAttributesSectionProps
> = ({ greyOutFields, mnos, termsAndConditions: notes }) => {
  const { control, trigger, clearErrors } =
    useFormContext<CampaignDetailsFormValues>();
  const {
    field: {
      disabled: subscriberOptinDisabled,
      value: subscriberOptin,
      onChange: setSubscriberOptin,
    },
    fieldState: { error: subscriberOptinError },
  } = useController({
    control,
    name: 'subscriberOptin',
    disabled: isFieldGreyOut(greyOutFields, 'subscriberOptin'),
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: optinKeywords, onChange: setOptinKeywords },
  } = useController({
    control,
    name: 'optinKeywords',
  });
  const {
    field: {
      value: optinMessage,
      onChange: setOptinMessage,
      onBlur: validateOptinMessage,
    },
    fieldState: { error: optinMessageError },
  } = useController({
    control,
    name: 'optinMessage',
    rules: {
      validate: {
        ...(subscriberOptin
          ? {
              isValidInput,
              withValidMinLength: createMinLengthValidator(20, true),
              withValidMaxLength: createMaxLengthValidator(1024, true),
            }
          : {}),
      },
    },
  });
  const {
    field: {
      disabled: embeddedLinkDisabled,
      value: embeddedLink,
      onChange: setEmbeddedLink,
    },
    fieldState: { error: embeddedLinkError },
  } = useController({
    control,
    name: 'embeddedLink',
    disabled: isFieldGreyOut(greyOutFields, 'embeddedLink'),
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: {
      value: embeddedLinkSample,
      onChange: setEmbeddedLinkSample,
      onBlur: validateEmbeddedLinkSample,
    },
    fieldState: { error: embeddedLinkSampleError },
  } = useController({
    control,
    name: 'embeddedLinkSample',
    disabled: !embeddedLink,
    rules: {
      validate: {
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(255),
      },
    },
  });
  const {
    field: {
      disabled: subscriberOptoutDisabled,
      value: subscriberOptout,
      onChange: setSubscriberOptout,
    },
    fieldState: { error: subscriberOptoutError },
  } = useController({
    control,
    name: 'subscriberOptout',
    disabled: isFieldGreyOut(greyOutFields, 'subscriberOptout'),
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: optoutKeywords, onChange: setOptoutKeywords },
  } = useController({
    control,
    name: 'optoutKeywords',
  });
  const {
    field: {
      value: optoutMessage,
      onChange: setOptoutMessage,
      onBlur: validateOptoutMessage,
    },
    fieldState: { error: optoutMessageError },
  } = useController({
    control,
    name: 'optoutMessage',
    rules: {
      validate: {
        ...(subscriberOptout
          ? {
              isValidInput,
              isRequired: createRequiredValidator('Please enter message'),
              withValidMinLength: createMinLengthValidator(20),
              withValidMaxLength: createMaxLengthValidator(1024),
            }
          : {}),
      },
    },
  });
  const {
    field: {
      disabled: embeddedPhoneDisabled,
      value: embeddedPhone,
      onChange: setEmbeddedPhone,
    },
    fieldState: { error: embeddedPhoneError },
  } = useController({
    control,
    name: 'embeddedPhone',
    disabled: isFieldGreyOut(greyOutFields, 'embeddedPhone'),
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: {
      disabled: subscriberHelpDisabled,
      value: subscriberHelp,
      onChange: setSubscriberHelp,
    },
    fieldState: { error: subscriberHelpError },
  } = useController({
    control,
    name: 'subscriberHelp',
    disabled: isFieldGreyOut(greyOutFields, 'subscriberHelp'),
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: helpKeywords, onChange: setHelpKeywords },
  } = useController({
    control,
    name: 'helpKeywords',
  });
  const {
    field: {
      value: helpMessage,
      onChange: setHelpMessage,
      onBlur: validateHelpMessage,
    },
    fieldState: { error: helpMessageError },
  } = useController({
    control,
    name: 'helpMessage',
    disabled: !subscriberHelp,
    rules: {
      validate: {
        ...(subscriberHelp
          ? {
              isValidInput,
              isRequired: createRequiredValidator('Please enter message'),
              withValidMinLength: createMinLengthValidator(20),
              withValidMaxLength: createMaxLengthValidator(1024),
            }
          : {}),
      },
    },
  });
  const {
    field: {
      disabled: ageGatedDisabled,
      value: ageGated,
      onChange: setAgeGated,
    },
    fieldState: { error: ageGatedError },
  } = useController({
    control,
    name: 'ageGated',
    disabled: isFieldGreyOut(greyOutFields, 'ageGated'),
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: {
      disabled: numberPoolDisabled,
      value: numberPool,
      onChange: setNumberPool,
    },
    fieldState: { error: numberPoolError },
  } = useController({
    control,
    name: 'numberPool',
    disabled: isFieldGreyOut(greyOutFields, 'numberPool'),
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: {
      disabled: directLendingDisabled,
      value: directLending,
      onChange: setDirectLending,
    },
    fieldState: { error: directLendingError },
  } = useController({
    control,
    name: 'directLending',
    disabled: isFieldGreyOut(greyOutFields, 'directLending'),
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: {
      disabled: termsAndConditionsDisabled,
      value: termsAndConditions,
      onChange: setTermsAndConditions,
    },
    fieldState: { error: termsAndConditionsError },
  } = useController({
    control,
    name: 'termsAndConditions',
    disabled: isFieldGreyOut(greyOutFields, 'termsAndConditions'),
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const [additionalSetupType, setAdditionalSetupType] = useState(
    AdditionalSetupType.None
  );
  const subscriberOptinRequired = useMemo(
    () => mnos.some((mno) => mno.reqSubscriberOptin),
    [mnos]
  );
  const subscriberOptoutRequired = useMemo(
    () => mnos.some((mno) => mno.reqSubscriberOptout),
    [mnos]
  );
  const subscriberHelpRequired = useMemo(
    () => mnos.some((mno) => mno.reqSubscriberHelp),
    [mnos]
  );

  const handleAdditionalSetupClose = () => {
    setAdditionalSetupType(AdditionalSetupType.None);
  };

  const handleAdditionSetupVisibilityChange = (
    type: AdditionalSetupType,
    open: boolean
  ) => {
    if (open) {
      setAdditionalSetupType(type);
    } else if (additionalSetupType === type) {
      // Prevent additional setup from closing when switching additional setup type
      setAdditionalSetupType(AdditionalSetupType.None);
    }
  };

  useEffect(() => {
    if (subscriberOptin === true) {
      setOptinKeywords(DEFAULT_CAMPAIGN_FOR_CREATION_FORM.optinKeywords);
      setOptinMessage(DEFAULT_CAMPAIGN_FOR_CREATION_FORM.optinMessage);
      trigger('optinMessage');
    } else if (subscriberOptin === false) {
      setOptinKeywords([]);
      setOptinMessage('');
      clearErrors('optinMessage');
    }
  }, [subscriberOptin]);
  useEffect(() => {
    if (subscriberOptout === true) {
      setOptoutKeywords(DEFAULT_CAMPAIGN_FOR_CREATION_FORM.optoutKeywords);
      setOptoutMessage(DEFAULT_CAMPAIGN_FOR_CREATION_FORM.optoutMessage);
      trigger('optoutMessage');
    } else if (subscriberOptout === false) {
      setOptoutKeywords([]);
      setOptoutMessage('');
      clearErrors('optoutMessage');
    }
  }, [subscriberOptout]);
  useEffect(() => {
    if (subscriberHelp === true) {
      setHelpKeywords(DEFAULT_CAMPAIGN_FOR_CREATION_FORM.helpKeywords);
      setHelpMessage(DEFAULT_CAMPAIGN_FOR_CREATION_FORM.helpMessage);
      trigger('helpMessage');
    } else if (subscriberHelp === false) {
      setHelpKeywords([]);
      setHelpMessage('');
      clearErrors('helpMessage');
    }
  }, [subscriberHelp]);
  useEffect(() => {
    if (embeddedLink === true) {
      setEmbeddedLinkSample(
        DEFAULT_CAMPAIGN_FOR_CREATION_FORM.embeddedLinkSample
      );
    } else if (embeddedLink === false) {
      setEmbeddedLinkSample('');
      clearErrors('embeddedLinkSample');
    }
  }, [embeddedLink]);

  return (
    <Grid
      item
      xs={12}
      data-testid="campaignDetailsFormCampaignAndContentAttributes"
    >
      <div className="campaign-attributes wrapper">
        <HeadingBlock icon={faSquarePollHorizontal} title="Campaign Attributes">
          <span>
            (For M2M (Machine-to-Machine) Use Case, please select No if the
            attributes are not applicable.)
          </span>
        </HeadingBlock>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 'l',
            rowGap: 's',
          }}
        >
          <CampaignAttributes.Attribute
            id="subscriberOptin"
            label="Subscriber Opt-in"
            tooltip="You are collecting and processing consumer opt-ins."
            open={additionalSetupType === AdditionalSetupType.SubscriberOptin}
            disabledOption={
              subscriberOptinDisabled
                ? 'all'
                : subscriberOptinRequired
                ? 'no'
                : undefined
            }
            value={subscriberOptin}
            error={subscriberOptinError?.message || optinMessageError?.message}
            additionalSetup={
              <MessageFlowAttributePopup
                showCharCounter
                label="Opt-in"
                keywordsTooltip="A keyword consumers can text to opt-in."
                messageTooltip="If consumers can text in a keyword, the response should include the Brand DBA name, confirmation of opt-in enrollment to a recurring message campaign, how to get help, and clear description of how to opt-out."
                keywords={optinKeywords}
                message={optinMessage}
                error={optinMessageError?.message}
                onKeywordsChange={setOptinKeywords}
                onMessageChange={setOptinMessage}
                onMessageBlur={validateOptinMessage}
                onClose={handleAdditionalSetupClose}
              />
            }
            onChange={setSubscriberOptin}
            onVisibilityChange={(open) =>
              handleAdditionSetupVisibilityChange(
                AdditionalSetupType.SubscriberOptin,
                open
              )
            }
          />
          <CampaignAttributes.Attribute
            id="embeddedLink"
            label="Embedded Link"
            tooltip="Are you using an embedded link of any kind? Note that public URL shorteners (bitly, tinyurl) are not accepted."
            open={additionalSetupType === AdditionalSetupType.EmbeddedLink}
            disabledOption={embeddedLinkDisabled ? 'all' : undefined}
            value={embeddedLink}
            error={
              embeddedLinkError?.message || embeddedLinkSampleError?.message
            }
            additionalSetup={
              <EmbeddedLinkPopup
                value={embeddedLinkSample}
                error={embeddedLinkSampleError?.message}
                onChange={setEmbeddedLinkSample}
                onBlur={validateEmbeddedLinkSample}
                onClose={handleAdditionalSetupClose}
              />
            }
            onChange={setEmbeddedLink}
            onVisibilityChange={(open) =>
              handleAdditionSetupVisibilityChange(
                AdditionalSetupType.EmbeddedLink,
                open
              )
            }
          />
          <CampaignAttributes.Attribute
            id="subscriberOptout"
            label="Subscriber Opt-Out"
            tooltip="You are collecting and processing consumer opt-outs."
            open={additionalSetupType === AdditionalSetupType.SubscriberOptout}
            disabledOption={
              subscriberOptoutDisabled
                ? 'all'
                : subscriberOptoutRequired
                ? 'no'
                : undefined
            }
            value={subscriberOptout}
            error={
              subscriberOptoutError?.message || optoutMessageError?.message
            }
            additionalSetup={
              <MessageFlowAttributePopup
                messageRequired
                showCharCounter
                label="Opt-out"
                keywordsTooltip="A keyword consumers can text to opt-out."
                messageTooltip="The response to the STOP keyword may include the Brand DBA name but should include an acknowledgement of opt-out request and confirmation that no further messages will be sent."
                error={optoutMessageError?.message}
                keywords={optoutKeywords}
                message={optoutMessage}
                onKeywordsChange={setOptoutKeywords}
                onMessageChange={setOptoutMessage}
                onMessageBlur={validateOptoutMessage}
                onClose={handleAdditionalSetupClose}
              />
            }
            onChange={setSubscriberOptout}
            onVisibilityChange={(open) =>
              handleAdditionSetupVisibilityChange(
                AdditionalSetupType.SubscriberOptout,
                open
              )
            }
          />
          <CampaignAttributes.Attribute
            id="embeddedPhone"
            label="Embedded Phone Number"
            tooltip="Are you using an embedded phone number (except the required HELP information contact phone number)?"
            disabledOption={embeddedPhoneDisabled ? 'all' : undefined}
            value={embeddedPhone}
            error={embeddedPhoneError?.message}
            onChange={setEmbeddedPhone}
          />
          <CampaignAttributes.Attribute
            id="subscriberHelp"
            label="Subscriber Help"
            tooltip="You have implemented message reply providing customers on how they can contact the message sender after they reply with the “HELP” keyword."
            open={additionalSetupType === AdditionalSetupType.SubscriberHelp}
            disabledOption={
              subscriberHelpDisabled
                ? 'all'
                : subscriberHelpRequired
                ? 'no'
                : undefined
            }
            value={subscriberHelp}
            error={subscriberHelpError?.message || helpMessageError?.message}
            additionalSetup={
              <MessageFlowAttributePopup
                messageRequired
                showCharCounter
                label="Help"
                keywordsTooltip="A keyword consumers use to get help or more information."
                messageTooltip="The response to HELP keyword may include the Brand DBA name and additional support contact information."
                error={helpMessageError?.message}
                keywords={helpKeywords}
                message={helpMessage}
                onKeywordsChange={setHelpKeywords}
                onMessageChange={setHelpMessage}
                onMessageBlur={validateHelpMessage}
                onClose={handleAdditionalSetupClose}
              />
            }
            onChange={setSubscriberHelp}
            onVisibilityChange={(open) =>
              handleAdditionSetupVisibilityChange(
                AdditionalSetupType.SubscriberHelp,
                open
              )
            }
          />
          <CampaignAttributes.Attribute
            id="ageGated"
            label="Age-Gated Content"
            tooltip="Will this campaign include any age-gated content as defined by Carrier and CTIA guidelines?"
            disabledOption={ageGatedDisabled ? 'all' : undefined}
            value={ageGated}
            error={ageGatedError?.message}
            onChange={setAgeGated}
          />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 'l',
            mt: 's',
          }}
        >
          <Flex sx={{ flexDirection: 'column', gap: 's' }}>
            <CampaignAttributes.Attribute
              id="numberPool"
              label="Number Pooling"
              tooltip="Select this if you intend on using 50+ numbers as this will require a different provisioning process on T-Mobile"
              disabledOption={numberPoolDisabled ? 'all' : undefined}
              value={numberPool}
              error={numberPoolError?.message}
              onChange={setNumberPool}
            />
            <CampaignAttributes.Attribute
              id="directLending"
              label="Direct Lending or Loan Arrangement"
              tooltip="Will this campaign include content related to direct lending or other loan arrangements?"
              disabledOption={directLendingDisabled ? 'all' : undefined}
              value={directLending}
              error={directLendingError?.message}
              onChange={setDirectLending}
            />
          </Flex>
          <CampaignAttributes.Attribute
            id="termsAndConditions"
            label="Terms & Conditions"
            tooltip="Indicates the campaign follows CTIA messaging principles and best practices"
            disabledOption={termsAndConditionsDisabled ? 'all' : 'no'}
            notes={notes}
            value={termsAndConditions}
            error={termsAndConditionsError?.message}
            onChange={setTermsAndConditions}
          />
        </Box>
      </div>
    </Grid>
  );
};

export default CampaignAttributesSection;
